import { useState, type ReactNode } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { Box, Stack, Typography } from '@mui/material';
import { webColors } from '@liminal/brand/semantic';
import { Card } from '@liminal/web-ui/primitives/Card';
import { Button } from '@liminal/web-ui/primitives/Button';
import { IconButton } from '@liminal/web-ui/primitives/IconButton';
import { PageHeader } from '@liminal/web-ui/display/PageHeader';
import { Input } from '@liminal/web-ui/forms/Input';
import { Select } from '@liminal/web-ui/forms/Select';
import { SegmentedToggle, type SegmentedOption } from '@liminal/web-ui/forms/SegmentedToggle';
import { Textarea } from '@liminal/web-ui/forms/Textarea';
import { FormErrorBanner } from '@liminal/web-ui/forms/FormErrorBanner';
import { ErrorState } from '@liminal/web-ui/feedback/ErrorState';
import { LoadingSpinner } from '@liminal/web-ui/feedback/LoadingSpinner';
import { isHttpError } from '@liminal/errors/http-errors';
import { MerchantIdentity_Type, RewardBudget_State, RewardMerchant_Category } from '@/api/generated';
import type { RewardMerchant } from '@/api/types';
import { getErrorMessage } from '@/errors/messages';
import {
  useCreateRewardMerchant,
  useRewardBudgets,
  useRewardMerchant,
  useUpdateRewardMerchant,
} from '@/hooks/useRewards';
import { useMerchantImageUpload } from '@/hooks/useMerchantImages';
import type { MerchantLogo, ServedImage } from '@/lib/merchantImages';
import {
  PAY_LEVEL_COUNT,
  cnpjDigits,
  groupDecimal,
  isCompleteCnpj,
  isMerchantCategory,
  parseDecimal,
  payLevelErrors,
  payLevelLil,
  resourceId,
} from '@/lib/rewards';
import { merchantCategory } from '@/components/ui/statusHelpers';
import { MerchantLogoField } from '../components/MerchantLogoField';
import { MerchantPhotosField } from '../components/MerchantPhotosField';
import { PartnerAppPreview } from '../components/PartnerAppPreview';
import { RatePreview } from './RatePreview';
import {
  NO_CATALOGUE_ERRORS,
  catalogueFields,
  changedCataloguePaths,
  toCatalogueForm,
  validateCatalogue,
  type CatalogueForm,
} from '../catalogueForm';
import { LinksEditor } from '../components/LinksEditor';
import { LocationsEditor } from '../components/LocationsEditor';
import { ActiveWindowsEditor } from '../components/ActiveWindowsEditor';

const CATEGORY_OPTIONS = Object.values(RewardMerchant_Category).map((category) => ({
  value: category,
  label: merchantCategory(category).label,
}));

/** The preview's three views: the partner's card, its page, and the choice on a payment. */
type PreviewView = 'card' | 'page' | 'payment';
const PREVIEW_VIEWS: readonly SegmentedOption<PreviewView>[] = [
  { value: 'card', label: 'Card' },
  { value: 'page', label: 'Page' },
  { value: 'payment', label: 'Payment' },
];

/** Budgets are a handful; one page offers every one. */
const BUDGET_OPTIONS_PAGE = 200;

const IDENTITY_TYPES = [
  { value: MerchantIdentity_Type.TYPE_CNPJ, label: 'CNPJ' },
  { value: MerchantIdentity_Type.TYPE_PIX_KEY, label: 'Pix key' },
];

/** Fields changed in place, and fields that make a new revision. Rewards takes each kind in its own request. */
const CATALOGUE_PATHS = ['display_name', 'description', 'images', 'logo'] as const;
const VERSIONED_PATHS = ['identities', 'rates', 'budget'] as const;

interface IdentityRow {
  readonly key: number;
  readonly type: MerchantIdentity_Type;
  readonly value: string;
}

interface FormState {
  readonly displayName: string;
  readonly description: string;
  /** The photos in display order, the main one first. */
  readonly images: readonly ServedImage[];
  readonly logo: MerchantLogo | null;
  readonly budget: string;
  readonly identities: readonly IdentityRow[];
  readonly redeemLilPerUsd: string;
  readonly earnLilPerUsd: string;
  /** The Pay with LIL levels' USD, one per row, PAY_LEVEL_COUNT rows; empty rows at a partner without them. */
  readonly payLevels: readonly string[];
  readonly reason: string;
}

/** The levels' placeholders: the design's $10 · $20 · $30. */
const PAY_LEVEL_PLACEHOLDERS = ['10', '20', '30'] as const;

/** No row marked: before the first save, and on a save that leaves the rates alone. */
const NO_LEVEL_ERRORS: readonly (string | undefined)[] = [];

function isIdentityType(value: string): value is MerchantIdentity_Type {
  return IDENTITY_TYPES.some((t) => t.value === value);
}

function toFormState(m: RewardMerchant | undefined): FormState {
  if (m?.logo && !m.logoUri) throw new Error(`merchant ${m.name} names a logo but not where it is served from`);
  const levels = m?.rates?.payLevels ?? [];
  if (levels.length > PAY_LEVEL_COUNT) {
    throw new Error(`merchant ${m?.name ?? ''} has ${levels.length} pay levels; the form holds ${PAY_LEVEL_COUNT}`);
  }
  return {
    displayName: m?.displayName ?? '',
    description: m?.description ?? '',
    images: (m?.images ?? []).map((image) => {
      if (!image.name || !image.uri) throw new Error(`merchant ${m?.name ?? ''} has an image without its name or URI`);
      return { ...image, name: image.name, uri: image.uri };
    }),
    logo: m?.logo && m.logoUri ? { name: m.logo, uri: m.logoUri } : null,
    budget: m?.budget ?? '',
    identities: (m?.identities ?? [{ type: MerchantIdentity_Type.TYPE_CNPJ, value: '' }]).map((identity, key) => {
      if (identity.type === undefined) throw new Error(`merchant ${m?.name ?? ''} has an identity without a type`);
      return { key, type: identity.type, value: identity.value ?? '' };
    }),
    redeemLilPerUsd: m?.rates?.redeemLilPerUsd ?? '',
    earnLilPerUsd: m?.rates?.earnLilPerUsd ?? '',
    // Always PAY_LEVEL_COUNT rows: a partner without levels opens with them empty, to fill on a rate change.
    payLevels: Array.from({ length: PAY_LEVEL_COUNT }, (_, index) => levels[index]?.usd ?? ''),
    reason: '',
  };
}

/** Whether the form changes the partner's rates, its levels among them: such a save makes a revision. */
function ratesChanged(form: FormState, original: FormState): boolean {
  return (
    form.redeemLilPerUsd.trim() !== original.redeemLilPerUsd ||
    form.earnLilPerUsd.trim() !== original.earnLilPerUsd ||
    form.payLevels.some((usd, index) => usd.trim() !== original.payLevels[index])
  );
}

/** The identities as Rewards stores them: a CNPJ as its fourteen digits. */
function storedIdentities(rows: readonly IdentityRow[]) {
  return rows.map((row) => ({
    type: row.type,
    value: row.type === MerchantIdentity_Type.TYPE_CNPJ ? cnpjDigits(row.value) : row.value.trim(),
  }));
}

/**
 * Why the form cannot be sent yet, or null when it can. The reason is checked
 * at save, once it is known whether the change makes a revision: catalogue
 * fields change in place and need none.
 */
function validate(form: FormState): string | null {
  if (form.displayName.trim() === '') return 'Give the partner a name.';
  if (form.budget === '') return 'Choose the budget it draws from.';
  if (form.identities.length === 0) return 'Add the CNPJ or Pix key its payments are matched by.';
  for (const row of form.identities) {
    if (row.type === MerchantIdentity_Type.TYPE_CNPJ && !isCompleteCnpj(row.value)) {
      return `"${row.value}" is not a complete CNPJ: it needs fourteen digits.`;
    }
    if (row.type === MerchantIdentity_Type.TYPE_PIX_KEY && row.value.trim() === '') return 'A Pix key cannot be empty.';
  }
  if (!parseDecimal(form.redeemLilPerUsd) || !parseDecimal(form.earnLilPerUsd)) {
    return 'Both rates must be decimals, such as 100 or 4.5.';
  }
  return null;
}

export function PartnerFormPage() {
  const { id } = useParams<{ id: string }>();
  const merchantId = id ?? '';
  const merchant = useRewardMerchant(merchantId);

  if (merchantId === '') return <PartnerForm />;
  if (merchant.isLoading) return <LoadingSpinner />;
  if (merchant.isError || !merchant.data) {
    return <ErrorState message={getErrorMessage(merchant.error)} onRetry={() => void merchant.refetch()} />;
  }
  return <PartnerForm key={merchant.data.etag} current={merchant.data} />;
}

function PartnerForm({ current }: { readonly current?: RewardMerchant }) {
  const navigate = useNavigate();
  const editing = current !== undefined;
  const [form, setForm] = useState<FormState>(() => toFormState(current));
  // Idempotency keys for this visit, kept across a retry, so a resubmit after
  // a timeout replays the save rather than making a second one. An edit sends
  // its catalogue and versioned fields as two updates, each with its own key.
  const [requestIds] = useState(() => ({
    create: crypto.randomUUID(),
    catalogue: crypto.randomUUID(),
    versioned: crypto.randomUUID(),
  }));
  const [nextKey, setNextKey] = useState(form.identities.length);
  const [error, setError] = useState('');
  // Category, links, locations and windows (LIM-4607). Their field errors
  // show from the first save attempt, then follow every edit. Only a new
  // partner must have a category; see validateCatalogue.
  const [catalogue, setCatalogue] = useState<CatalogueForm>(() => toCatalogueForm(current));
  const [submitted, setSubmitted] = useState(false);
  const [previewView, setPreviewView] = useState<PreviewView>('page');
  const catalogueErrors = validateCatalogue(catalogue, !editing);
  const shownErrors = submitted ? catalogueErrors : NO_CATALOGUE_ERRORS;
  // The levels are wanted on a new partner and on any save that touches the
  // rates (P-LIM-222), so a name edit at a partner without them never waits
  // on them. Like the catalogue's, their marks show from the first save.
  const levelsWanted = !current || ratesChanged(form, toFormState(current));
  const levelErrors = levelsWanted ? payLevelErrors(form.payLevels) : NO_LEVEL_ERRORS;
  const shownLevelErrors = submitted ? levelErrors : NO_LEVEL_ERRORS;
  const firstLevelError = levelErrors.findIndex((levelError) => levelError !== undefined);
  const budgets = useRewardBudgets(BUDGET_OPTIONS_PAGE, '');
  const create = useCreateRewardMerchant();
  const update = useUpdateRewardMerchant();
  const saving = create.isPending || update.isPending;
  // Photos and the logo upload as soon as they are picked, which needs the
  // merchant's id; each lands in the form, and Save writes the order and the logo.
  const merchantId = current ? resourceId(current.name, 'rewardMerchants') : '';
  const photoUploads = useMerchantImageUpload({
    merchantId,
    kind: 'photo',
    onAttached: (image) => setForm((f) => ({ ...f, images: [...f.images, image] })),
  });
  const logoUploads = useMerchantImageUpload({
    merchantId,
    kind: 'logo',
    onAttached: (image) => setForm((f) => ({ ...f, logo: { name: image.name, uri: image.uri } })),
  });

  const set = (patch: Partial<FormState>) => setForm((f) => ({ ...f, ...patch }));
  const setIdentity = (key: number, patch: Partial<IdentityRow>) =>
    set({ identities: form.identities.map((row) => (row.key === key ? { ...row, ...patch } : row)) });

  // A closed budget admits no choice, so only open and paused ones are offered,
  // plus the one the partner already has, so an edit never silently drops it.
  const budgetOptions = (budgets.data?.rewardBudgets ?? [])
    .filter((b) => b.state !== RewardBudget_State.STATE_CLOSED || b.name === current?.budget)
    .map((b) => ({ value: b.name, label: b.displayName ?? b.name }));
  const noBudgets = budgets.isSuccess && budgetOptions.length === 0;

  const handleSave = async () => {
    setSubmitted(true);
    const invalid =
      validate(form) ??
      (firstLevelError === -1 ? null : `Pay with LIL level ${firstLevelError + 1}: ${levelErrors[firstLevelError]}`) ??
      catalogueErrors.first;
    if (invalid) {
      setError(invalid);
      return;
    }
    if (photoUploads.busy || logoUploads.busy) {
      setError('Wait for the photos to finish uploading.');
      return;
    }
    setError('');
    const levels = form.payLevels.map((usd) => usd.trim()).filter((usd) => usd !== '');
    const merchant: Omit<RewardMerchant, 'name'> = {
      displayName: form.displayName.trim(),
      description: form.description.trim(),
      // Each image by its name, in the order wanted; images need a saved partner.
      ...(current ? { images: form.images.map((image) => ({ name: image.name })), logo: form.logo?.name ?? '' } : {}),
      budget: form.budget,
      identities: storedIdentities(form.identities),
      rates: {
        redeemLilPerUsd: form.redeemLilPerUsd.trim(),
        earnLilPerUsd: form.earnLilPerUsd.trim(),
        // The levels as typed, none at a partner without them. A save that
        // touches the rates has every row (levelErrors); one that does not
        // sends the saved levels, never an empty row the server would refuse.
        ...(levels.length > 0 ? { payLevels: levels.map((usd) => ({ usd })) } : {}),
      },
      revisionReason: form.reason.trim(),
      ...catalogueFields(catalogue),
    };
    try {
      if (!current) {
        if (merchant.revisionReason === '') {
          setError('Say why the partner is being added.');
          return;
        }
        const created = await create.mutateAsync({ merchant, requestId: requestIds.create });
        navigate(`/rewards/partners/${resourceId(created.name, 'rewardMerchants')}`);
        return;
      }
      const id = resourceId(current.name, 'rewardMerchants');
      const original = toFormState(current);
      const changed = {
        display_name: merchant.displayName !== original.displayName,
        description: merchant.description !== original.description,
        images: JSON.stringify(form.images.map((i) => i.name)) !== JSON.stringify(original.images.map((i) => i.name)),
        logo: (form.logo?.name ?? '') !== (original.logo?.name ?? ''),
        identities: JSON.stringify(merchant.identities) !== JSON.stringify(storedIdentities(original.identities)),
        rates: ratesChanged(form, original),
        budget: merchant.budget !== original.budget,
      };
      const catalogueMask = [
        ...CATALOGUE_PATHS.filter((path) => changed[path]),
        ...changedCataloguePaths(catalogue, toCatalogueForm(current)),
      ];
      const versionedMask = VERSIONED_PATHS.filter((path) => changed[path]);
      if (catalogueMask.length === 0 && versionedMask.length === 0) {
        setError('Nothing has changed.');
        return;
      }
      // Only a change that makes a revision records a reason.
      if (versionedMask.length > 0 && merchant.revisionReason === '') {
        setError('Say why you are changing the identities, rates or budget.');
        return;
      }
      let etag = current.etag;
      if (catalogueMask.length > 0) {
        const saved = await update.mutateAsync({
          id,
          merchant: { ...merchant, name: current.name, etag },
          updateMask: catalogueMask,
          requestId: requestIds.catalogue,
        });
        etag = saved.etag;
      }
      if (versionedMask.length > 0) {
        await update.mutateAsync({
          id,
          merchant: { ...merchant, name: current.name, etag },
          updateMask: versionedMask,
          requestId: requestIds.versioned,
        });
      }
      navigate(`/rewards/partners/${id}`);
    } catch (e) {
      setError(
        isHttpError(e) && e.status === 409
          ? 'This partner was changed by someone else. Reload it and make your change again.'
          : getErrorMessage(e),
      );
    }
  };

  const backTo = current ? `/rewards/partners/${resourceId(current.name, 'rewardMerchants')}` : '/rewards/partners';

  return (
    <Stack spacing={3}>
      <Box>
        <Button variant="link" size="sm" startIcon="arrow-back" onClick={() => navigate(backTo)} disabled={saving}>
          {current ? (current.displayName ?? 'Partner') : 'Partners'}
        </Button>
      </Box>
      <PageHeader
        title={current ? `Edit ${current.displayName ?? 'partner'}` : 'New partner'}
        autoTitleCase={false}
        subtitle="A merchant whose Pix payments let the customer earn LIL or pay with it."
      />

      <Box sx={{ display: 'grid', gridTemplateColumns: { xs: '1fr', md: '3fr 2fr' }, gap: 3, alignItems: 'start' }}>
        <Card variant="elevated">
          <Stack spacing={4}>
            <Section title="About" detail="What customers see on the partner's card.">
              <Input label="Name" value={form.displayName} onChange={(displayName) => set({ displayName })} required />
              <Textarea
                label="Description"
                value={form.description}
                onChange={(description) => set({ description })}
                helper="One short line"
                rows={2}
              />
            </Section>

            <Section title="Photos" detail="The first is the main photo, shown on the partner's card in the app.">
              <MerchantPhotosField
                merchantSaved={editing}
                images={form.images}
                onReorder={(images) => set({ images })}
                onDeleted={(name) => setForm((f) => ({ ...f, images: f.images.filter((image) => image.name !== name) }))}
                uploads={photoUploads}
                disabled={saving}
              />
            </Section>

            <Section title="Logo" detail="The partner's mark, over the bottom-right corner of its card photo in the app.">
              <MerchantLogoField
                merchantSaved={editing}
                logo={form.logo}
                onRemove={() => set({ logo: null })}
                uploads={logoUploads}
                disabled={saving}
              />
            </Section>

            <Section title="Category" detail="Where the app files the partner, and the chip on its card.">
              <Select
                aria-label="Category"
                value={catalogue.category}
                onChange={(category) => {
                  if (!isMerchantCategory(category)) throw new Error(`unknown category ${category}`);
                  setCatalogue((c) => ({ ...c, category }));
                }}
                options={CATEGORY_OPTIONS}
                placeholder="Choose a category"
                error={shownErrors.category}
              />
            </Section>

            <Section title="Links" detail="Buttons on the partner's page. A Google Maps link also adds its place to Locations.">
              <LinksEditor form={catalogue} setForm={setCatalogue} errors={shownErrors.links} />
            </Section>

            <Section
              title="Locations"
              detail='Where customers can visit, each pinned on the map. Paste a Google Maps link into each row, or enter the address and coordinates, where "lat, lng" pasted into a coordinate fills both.'
            >
              <LocationsEditor form={catalogue} setForm={setCatalogue} errors={shownErrors.locations} />
            </Section>

            <Section title="Active windows" detail="When payments at the partner qualify for a reward. Outside every window it is hidden in the app.">
              <ActiveWindowsEditor form={catalogue} setForm={setCatalogue} errors={shownErrors.windows} />
            </Section>

            <Section
              title="Which payments count"
              detail="A Pix payment to any of these is this partner's. Add its CNPJ, and any Pix key it is paid to."
            >
              <Stack spacing={1}>
                {form.identities.map((row) => (
                  <Stack key={row.key} direction="row" spacing={2} alignItems="flex-start">
                    <Box sx={{ width: 140, flexShrink: 0 }}>
                      <Select
                        aria-label="Identity type"
                        value={row.type}
                        onChange={(type) => {
                          if (!isIdentityType(type)) throw new Error(`unknown identity type ${type}`);
                          setIdentity(row.key, { type });
                        }}
                        options={IDENTITY_TYPES}
                        width={140}
                      />
                    </Box>
                    <Box sx={{ flex: 1, minWidth: 0 }}>
                      <Input
                        aria-label="Identity value"
                        value={row.value}
                        onChange={(value) => setIdentity(row.key, { value })}
                        placeholder={row.type === MerchantIdentity_Type.TYPE_CNPJ ? '11.222.333/0001-81' : 'Email, phone, CPF/CNPJ or random key'}
                        helper={
                          row.type === MerchantIdentity_Type.TYPE_CNPJ && row.value !== ''
                            ? `${cnpjDigits(row.value).length} of 14 digits`
                            : undefined
                        }
                      />
                    </Box>
                    <Box sx={{ height: 56, display: 'flex', alignItems: 'center' }}>
                      <IconButton
                        icon="close"
                        label="Remove identity"
                        size="sm"
                        tone="muted"
                        onClick={() => set({ identities: form.identities.filter((r) => r.key !== row.key) })}
                        disabled={form.identities.length <= 1}
                      />
                    </Box>
                  </Stack>
                ))}
                <Stack direction="row">
                  <Button
                    variant="link"
                    size="sm"
                    startIcon="plus"
                    onClick={() => {
                      set({ identities: [...form.identities, { key: nextKey, type: MerchantIdentity_Type.TYPE_PIX_KEY, value: '' }] });
                      setNextKey(nextKey + 1);
                    }}
                  >
                    Add another
                  </Button>
                </Stack>
              </Stack>
            </Section>

            <Section title="Rates" detail="How much LIL a customer spends or gets, per dollar of the payment.">
              <Stack direction={{ xs: 'column', sm: 'row' }} spacing={2}>
                <Input
                  label="Pay with LIL: LIL per $1 back"
                  value={form.redeemLilPerUsd}
                  onChange={(redeemLilPerUsd) => set({ redeemLilPerUsd })}
                  inputMode="decimal"
                  placeholder="100"
                  helper="LIL the customer spends to get $1 back"
                  required
                />
                <Input
                  label="Earn: LIL per $1 spent"
                  value={form.earnLilPerUsd}
                  onChange={(earnLilPerUsd) => set({ earnLilPerUsd })}
                  inputMode="decimal"
                  placeholder="5"
                  helper="LIL the customer gets instead. Lower than the Pay rate"
                  required
                />
              </Stack>
              <Stack spacing={0.5}>
                <Typography variant="subtitle2">Pay with LIL levels</Typography>
                <Typography variant="body2" color="text.secondary">
                  The dollars a customer can get back on one payment, each costing its dollars at the Pay rate. A payment
                  offers the levels below it and the first that reaches it. Needed on a new partner and whenever the rates
                  change.
                </Typography>
              </Stack>
              <Stack direction={{ xs: 'column', sm: 'row' }} spacing={2}>
                {form.payLevels.map((usd, index) => {
                  const lil = payLevelLil(usd, form.redeemLilPerUsd);
                  return (
                    <Input
                      key={index}
                      label={`Level ${index + 1} (USD)`}
                      value={usd}
                      onChange={(value) => set({ payLevels: form.payLevels.map((row, i) => (i === index ? value : row)) })}
                      inputMode="decimal"
                      placeholder={PAY_LEVEL_PLACEHOLDERS[index]}
                      helper={lil === null ? 'LIL at the Pay rate' : `Costs ${groupDecimal(lil)} LIL`}
                      error={shownLevelErrors[index]}
                    />
                  );
                })}
              </Stack>
            </Section>

            <Section title="Budget" detail="Caps how much Pay with LIL may pay back at this partner.">
              {noBudgets ? (
                <Stack direction="row" spacing={2} alignItems="center">
                  <Typography variant="body2" color="error">
                    There is no open budget yet.
                  </Typography>
                  <Button variant="secondary" size="sm" startIcon="plus" onClick={() => navigate('/rewards/budgets/new')}>
                    New budget
                  </Button>
                </Stack>
              ) : (
                <Select
                  aria-label="Budget"
                  value={form.budget}
                  onChange={(budget) => set({ budget })}
                  options={budgetOptions}
                  placeholder={budgets.isLoading ? 'Loading budgets…' : 'Choose a budget'}
                  error={budgets.isError ? getErrorMessage(budgets.error) : undefined}
                />
              )}
            </Section>

            <Section title={editing ? 'Why this change' : 'Why add it'}>
              <Textarea
                aria-label="Reason"
                value={form.reason}
                onChange={(reason) => set({ reason })}
                helper={
                  editing
                    ? 'Needed when identities, rates or the budget change. Kept in the change history with your name'
                    : 'Kept in the change history with your name'
                }
                rows={2}
              />
            </Section>

            <FormErrorBanner error={error} />

            <Stack direction="row" spacing={2} justifyContent="flex-end">
              <Button variant="link" onClick={() => navigate(backTo)} disabled={saving}>
                Cancel
              </Button>
              <Button onClick={() => void handleSave()} loading={saving}>
                {editing ? 'Save changes' : 'Add partner'}
              </Button>
            </Stack>
          </Stack>
        </Card>

        <Box sx={{ position: { md: 'sticky' }, top: { md: 24 } }}>
          <Card variant="elevated">
            <Stack spacing={2}>
              <Typography variant="h6">What the customer sees</Typography>
              <SegmentedToggle
                aria-label="Preview"
                size="sm"
                options={PREVIEW_VIEWS}
                value={previewView}
                onChange={setPreviewView}
              />
              {previewView === 'payment' ? (
                <RatePreview
                  merchantName={form.displayName}
                  redeemLilPerUsd={form.redeemLilPerUsd}
                  earnLilPerUsd={form.earnLilPerUsd}
                  payLevels={form.payLevels}
                />
              ) : (
                <>
                  <PartnerAppPreview
                    view={previewView}
                    displayName={form.displayName}
                    description={form.description}
                    images={form.images}
                    logoUri={form.logo?.uri ?? null}
                    category={catalogue.category}
                    earnLilPerUsd={form.earnLilPerUsd}
                    redeemLilPerUsd={form.redeemLilPerUsd}
                    payLevels={form.payLevels}
                    locations={catalogue.locations}
                    links={catalogue.links}
                  />
                  <Typography variant="caption" color="text.secondary">
                    {previewView === 'card'
                      ? "The partner's card on the app's Rewards page, as you type."
                      : "The partner's page in the app, as you type. Fonts and photo cropping can differ slightly on a phone; the map above its places is not drawn here."}
                  </Typography>
                </>
              )}
            </Stack>
          </Card>
        </Box>
      </Box>
    </Stack>
  );
}

/** A titled group of the form's fields, with a line on what they decide. */
function Section({ title, detail, children }: { readonly title: string; readonly detail?: string; readonly children: ReactNode }) {
  return (
    <Stack spacing={2} sx={{ pt: 3, borderTop: `1px solid ${webColors.border.light}`, '&:first-of-type': { pt: 0, borderTop: 'none' } }}>
      <Stack spacing={0.5}>
        <Typography variant="subtitle1">{title}</Typography>
        {detail && (
          <Typography variant="body2" color="text.secondary">
            {detail}
          </Typography>
        )}
      </Stack>
      {children}
    </Stack>
  );
}
